import React from 'react';
import {
    ArrowLeft,
    ArrowRight,
    BadgeCheck,
    Bell,
    BookOpen,
    Brain,
    BriefcaseBusiness,
    Check,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    CircleCheck,
    CircleHelp,
    Clock,
    CodeXml,
    Columns3,
    Eye,
    EyeOff,
    FileText,
    Flag,
    Folder,
    FolderPlus,
    GitMerge,
    GraduationCap,
    Hash,
    Headphones,
    HeartPulse,
    History,
    House,
    Image,
    Info,
    KeyRound,
    LayoutGrid,
    List,
    ListOrdered,
    Lock,
    Megaphone,
    MessageSquare,
    Minus,
    PanelLeft,
    Pause,
    Pencil,
    Play,
    Plus,
    Quote,
    RefreshCw,
    Scale,
    Search,
    SendHorizontal,
    Settings,
    Shield,
    ShieldCheck,
    ShoppingCart,
    SlidersHorizontal,
    Sparkle,
    Sparkles,
    SquareCheckBig,
    ThumbsDown,
    ThumbsUp,
    Trash2,
    TriangleAlert,
    Upload,
    User,
    Users,
    Wallet,
    Wrench,
    X,
} from 'lucide-react';

/**
 * Every `lucide:<name>` the prototype (Inbox.dc.html) uses in the Wiki + AI
 * Brain markup (26,500–196,600) and logic/seed data (693,280–882,306),
 * enumerated from the source on 2026-10-06 — 67 names. Keys are the prototype
 * names WITHOUT the `lucide:` prefix (the backend stores category icons that
 * way, e.g. `shopping-cart`). Values are lucide-react 0.507 components (the
 * canonical names: `TriangleAlert` for `alert-triangle`, `House` for `house`).
 */
export const WIKI_ICONS = Object.freeze({
    'alert-triangle': TriangleAlert,
    'arrow-left': ArrowLeft,
    'arrow-right': ArrowRight,
    'badge-check': BadgeCheck,
    bell: Bell,
    'book-open': BookOpen,
    brain: Brain,
    'briefcase-business': BriefcaseBusiness,
    check: Check,
    'chevron-down': ChevronDown,
    'chevron-right': ChevronRight,
    'chevron-up': ChevronUp,
    'circle-check': CircleCheck,
    'circle-help': CircleHelp,
    clock: Clock,
    'code-xml': CodeXml,
    'columns-3': Columns3,
    eye: Eye,
    'eye-off': EyeOff,
    'file-text': FileText,
    flag: Flag,
    folder: Folder,
    'folder-plus': FolderPlus,
    'git-merge': GitMerge,
    'graduation-cap': GraduationCap,
    hash: Hash,
    headphones: Headphones,
    'heart-pulse': HeartPulse,
    history: History,
    house: House,
    image: Image,
    info: Info,
    'key-round': KeyRound,
    'layout-grid': LayoutGrid,
    list: List,
    'list-ordered': ListOrdered,
    lock: Lock,
    megaphone: Megaphone,
    'message-square': MessageSquare,
    minus: Minus,
    'panel-left': PanelLeft,
    pause: Pause,
    pencil: Pencil,
    play: Play,
    plus: Plus,
    quote: Quote,
    'refresh-cw': RefreshCw,
    scale: Scale,
    search: Search,
    'send-horizontal': SendHorizontal,
    settings: Settings,
    shield: Shield,
    'shield-check': ShieldCheck,
    'shopping-cart': ShoppingCart,
    'sliders-horizontal': SlidersHorizontal,
    sparkle: Sparkle,
    sparkles: Sparkles,
    'square-check-big': SquareCheckBig,
    'thumbs-down': ThumbsDown,
    'thumbs-up': ThumbsUp,
    'trash-2': Trash2,
    upload: Upload,
    user: User,
    users: Users,
    wallet: Wallet,
    wrench: Wrench,
    x: X,
});

/** The New-category modal's icon picker, in prototype order (logic 739,654). */
export const WIKI_CATEGORY_ICON_CHOICES = Object.freeze([
    'folder', 'shopping-cart', 'headphones', 'users', 'megaphone',
    'shield-check', 'scale', 'wallet', 'wrench', 'graduation-cap',
]);

/** `"lucide:shopping-cart"` / `"shopping-cart"` / `" Shopping-Cart "` → `"shopping-cart"`. */
export function wikiIconName(name) {
    if (typeof name !== 'string') return '';
    return name.trim().toLowerCase().replace(/^lucide:/, '');
}

function isComponent(value) {
    return typeof value === 'function' || Boolean(value && typeof value === 'object' && value.$$typeof);
}

/**
 * Resolve a prototype icon name (with or without `lucide:`) to a lucide-react
 * component. A component passes straight through. Unknown → `FileText`.
 */
export function resolveWikiIcon(name) {
    if (isComponent(name)) return name;
    return WIKI_ICONS[wikiIconName(name)] || FileText;
}

/**
 * `<WikiIcon name="lucide:shopping-cart" size={13} />`
 *
 * `size` is the prototype's `l-icon` font-size in px. Decorative by default
 * (aria-hidden); pass `title` to make it an accessible image.
 */
export default function WikiIcon({ name, size = 16, strokeWidth, className = '', style, title, ...rest }) {
    const Icon = resolveWikiIcon(name);
    const a11y = title
        ? { role: 'img', 'aria-label': title }
        : { 'aria-hidden': true, focusable: 'false' };
    return (
        <Icon
            size={size}
            strokeWidth={strokeWidth}
            className={`wk-icon ${className}`.trim()}
            style={{ flex: '0 0 auto', ...style }}
            data-wiki-icon={isComponent(name) ? undefined : wikiIconName(name) || 'file-text'}
            {...a11y}
            {...rest}
        />
    );
}

// Prototype `iconWrap(c, size)` pairs: square size → icon size.
const SQUARE_ICON_SIZE = { 22: 13, 34: 17, 44: 21 };

/**
 * The prototype's coloured category square (`iconWrap`): `size`×`size`,
 * radius `round(size × 0.28)`, `tint` background, `color` foreground.
 * Nav rows use 22 (icon 13), Home cards 34 (icon 17), the category header 44
 * (icon 21).
 */
export function WikiIconSquare({ icon, color, tint, size = 22, iconSize, className = '', style, title }) {
    const inner = iconSize || SQUARE_ICON_SIZE[size] || Math.round(size * 0.5);
    return (
        <span
            className={`wk-icon-square ${className}`.trim()}
            title={title}
            style={{
                width: size,
                height: size,
                flex: `0 0 ${size}px`,
                borderRadius: Math.round(size * 0.28),
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: tint || 'var(--wk-fill, #F4F2EE)',
                color: color || 'var(--ink-2)',
                boxSizing: 'border-box',
                ...style,
            }}
        >
            <WikiIcon name={icon || 'folder'} size={inner} />
        </span>
    );
}
